import {recordRumAction} from '../datadogRum';
import {
  type PerfMarkContext,
  type PerfMarkDetail,
  type PerformanceDurationContract,
  type PerformanceDurationContractEntry,
  type PerformanceDurationMarkContract,
} from './types';

type AddPerfMarkOptions = {
  context: PerfMarkContext;
  devtools?: PerfMarkDetail['devtools'];
  operationKey?: string;
  startTime?: number;
};

/**
 * Duration marks are start/end boundaries for registered duration contracts
 * They never record action events directly
 */
type AddDurationPerfMarkOptions = AddPerfMarkOptions & {
  scopeStartTime: number;
};

/**
 * Single marks are standalone trace points
 * Single mark contracts can map to an actionName
 */
type PerformanceSingleMarkContract = Readonly<{
  actionName?: string;
  markName: string;
  recordPerfMark: (options: AddPerfMarkOptions) => PerformanceMark | undefined;
}>;

type DefinedPerformanceDurationContract = PerformanceDurationContract &
  Readonly<{
    durationMarkEntries: readonly PerformanceDurationContractEntry[];
    recordEndPerfMark: (
      options: AddDurationPerfMarkOptions
    ) => PerformanceMark | undefined;
    recordStartPerfMark: (
      options: AddDurationPerfMarkOptions
    ) => PerformanceMark | undefined;
  }>;

/**
 * Defines a duration with its start/end recorders
 * Keeps the paired marks next to the duration contract
 */
export function definePerfDuration(
  definition: PerformanceDurationContract
): DefinedPerformanceDurationContract {
  const startMark: PerformanceDurationMarkContract = {
    contract: definition,
    markName: definition.startMark,
    phase: 'start',
  };

  const endMark: PerformanceDurationMarkContract = {
    contract: definition,
    markName: definition.endMark,
    phase: 'end',
  };

  return {
    ...definition,
    durationMarkEntries: [
      [startMark.markName, startMark],
      [endMark.markName, endMark],
    ],
    recordEndPerfMark(
      options: AddDurationPerfMarkOptions
    ): PerformanceMark | undefined {
      return addPerfDurationMark(endMark, options);
    },
    recordStartPerfMark(
      options: AddDurationPerfMarkOptions
    ): PerformanceMark | undefined {
      return addPerfDurationMark(startMark, options);
    },
  };
}

/**
 * Defines a single mark with its recorder
 * Keeps action behavior next to the mark declaration
 */
export function definePerfSingleMark(
  definition: Readonly<{actionName?: string; markName: string}>
): PerformanceSingleMarkContract {
  const contract: PerformanceSingleMarkContract = {
    ...definition,
    recordPerfMark(options: AddPerfMarkOptions): PerformanceMark | undefined {
      return addPerfSingleMark(contract, options);
    },
  };
  return contract;
}

function addPerfDurationMark(
  mark: PerformanceDurationMarkContract,
  options: AddDurationPerfMarkOptions
): PerformanceMark | undefined {
  const {context, devtools, operationKey, startTime} = options;
  const detail: PerfMarkDetail = {
    __wandbPerformanceMark: true,
    context,
    devtools,
    markType: 'duration',
    operationKey,
    // Prevents marks from pairing across timing scopes
    scopeStartTime: options.scopeStartTime,
  };

  return createBrowserPerfMark(mark.markName, detail, startTime);
}

function addPerfSingleMark(
  mark: Readonly<{actionName?: string; markName: string}>,
  options: AddPerfMarkOptions
): PerformanceMark | undefined {
  const {context, devtools, operationKey, startTime} = options;
  const detail: PerfMarkDetail = {
    __wandbPerformanceMark: true,
    context,
    devtools,
    markType: 'single',
    operationKey,
  };

  const perfMark = createBrowserPerfMark(mark.markName, detail, startTime);

  if (mark.actionName != null) {
    // Records only explicit user-action marks
    recordRumAction(mark.actionName, context);
  }

  return perfMark;
}

function createBrowserPerfMark(
  markName: string,
  detail: PerfMarkDetail,
  startTime: number | undefined
): PerformanceMark | undefined {
  let mark: PerformanceMark | undefined;
  if (
    typeof performance !== 'undefined' &&
    typeof performance.mark === 'function'
  ) {
    try {
      // Keeps pairing data on the mark instead of in product UI state
      // https://developer.mozilla.org/en-US/docs/Web/API/Performance/mark#creating_markers_with_details
      mark = performance.mark(markName, {
        detail,
        startTime,
      });
    } catch {
      mark = undefined;
    }
  }

  return mark;
}
